import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import type { WorkingHoursConfig, DayKey } from '@/features/intake/types/workingHours';
import { DEFAULT_WORKING_HOURS, DAY_LABELS } from '@/features/intake/types/workingHours';

interface WorkingHoursSettingsProps {
  value: WorkingHoursConfig;
  onChange: (config: WorkingHoursConfig) => void;
  persisted?: boolean;
}

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

export function WorkingHoursSettings({ value, onChange, persisted = true }: WorkingHoursSettingsProps) {
  const config = value || DEFAULT_WORKING_HOURS;
  const hasEnabledDay = Object.values(config.schedule).some((d) => d.enabled);

  const updateDay = (day: DayKey, patch: Partial<WorkingHoursConfig['schedule'][DayKey]>) => {
    onChange({
      ...config,
      schedule: {
        ...config.schedule,
        [day]: { ...config.schedule[day], ...patch },
      },
    });
  };

  return (
    <div className="space-y-4">
      {!persisted && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
          Working hours are not saved yet. Customers will only see <strong>Record a Video</strong> on your
          public link until you enable days below and click <strong>Save Changes</strong>.
        </div>
      )}
      {persisted && !hasEnabledDay && (
        <div className="rounded-lg border border-muted px-4 py-3 text-sm text-muted-foreground">
          No days are enabled — only <strong>Record a Video</strong> is available on your public intake link.
        </div>
      )}
      <p className="text-sm text-muted-foreground">
        Set when customers can schedule video calls through your public intake link.
        If no hours are configured, only the &quot;Record a Video&quot; option will be shown.
      </p>

      <div className="space-y-2">
        <Label>Timezone</Label>
        <Select
          value={config.timezone}
          onValueChange={(tz) => onChange({ ...config, timezone: tz })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONES.map((tz) => (
              <SelectItem key={tz} value={tz}>
                {tz.replace(/_/g, ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {(Object.keys(DAY_LABELS) as DayKey[]).map((day) => {
          const dayConfig = config.schedule[day];
          return (
            <div
              key={day}
              className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0"
            >
              <Switch
                checked={dayConfig.enabled}
                onCheckedChange={(enabled) => updateDay(day, { enabled })}
              />
              <span className="w-24 text-sm font-medium">{DAY_LABELS[day]}</span>
              {dayConfig.enabled && (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    type="time"
                    value={dayConfig.start}
                    onChange={(e) => updateDay(day, { start: e.target.value })}
                    className="w-32 text-sm"
                  />
                  <span className="text-muted-foreground text-sm">to</span>
                  <Input
                    type="time"
                    value={dayConfig.end}
                    onChange={(e) => updateDay(day, { end: e.target.value })}
                    className="w-32 text-sm"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
